import { Box, Icon, Theme, Typography } from "@outschool/backpack";
import {
  ClassDetailsNoSectionsQuery,
  TeacherCertificateDiscipline,
} from "@outschool/gql-frontend-generated";
import { faUser } from "@outschool/icons";
import { useMachineTranslation, useTranslation } from "@outschool/localization";
import { leaderPath } from "@outschool/routes";
import { useTrackEvent } from "@outschool/ui-analytics";
import {
  TrackedButton,
  useVideoPlayerComponent,
} from "@outschool/ui-components-shared";
import { TeacherNameAndRatings } from "@outschool/ui-components-website";
import { pxToRem, useIsMobile, useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import { AskAQuestionButton } from "../ctas/AskAQuestionButton";
import Credentials from "./Credentials";
import ShowMoreModal from "./ShowMoreModal";

type TeacherDetailsProps = {
  leader: ClassDetailsNoSectionsQuery["activity"]["leader"];
  activity: ClassDetailsNoSectionsQuery["activity"];
  collapseText?: boolean;
  hideActions?: boolean;
  onClickViewTeacher?: (event: React.SyntheticEvent) => void;
  onClickViewReviews?: (event: React.SyntheticEvent) => void;
  credentialsRef?: React.RefObject<HTMLElement>;
  gradientStyles?: any;
  expandExpertise?: boolean;
};

export default React.forwardRef<any, TeacherDetailsProps>(
  (
    {
      leader,
      activity,
      collapseText = true,
      hideActions,
      onClickViewTeacher,
      onClickViewReviews,
      credentialsRef,
      gradientStyles,
      expandExpertise = false,
    },
    ref,
  ) => {
    const { t } = useTranslation(
      "client\\components\\detailPage\\subcomponents\\TeacherDetails",
    );
    const { translate } = useMachineTranslation();
    const translatedTeacherExpertise = translate(activity?.why_me || "");
    const translatedAboutText = translate(leader.details.about || "");
    const VideoPlayer = useVideoPlayerComponent();
    const isMobile = useIsMobile();
    const trackEvent = useTrackEvent();
    const Link = useLinkComponent();

    if (!leader) {
      return null;
    }

    const credentials = !!leader.credentials
      ? leader.credentials.filter(credential => {
          return (
            credential.degreeType ||
            (credential.certificateDiscipline &&
              (credential.certificateDiscipline !==
                TeacherCertificateDiscipline.Other ||
                (credential.certificateDiscipline ===
                  TeacherCertificateDiscipline.Other &&
                  credential.certificateState)))
          );
        })
      : [];

    const video = !!leader.video ? (
      <Box
        ref={ref}
        sx={(theme: Theme) => ({
          position: "relative",
          borderRadius: "1.6rem",
          height: "21.5rem",
          width: "28rem",
          overflow: "hidden",
          [theme.breakpoints.down("md")]: {
            width: "100%",
            height: "26.8rem",
          },
        })}
      >
        <VideoPlayer
          sx={{
            width: "100%",
            background: "linear-gradient(to top, #000000, #ffffff)",
          }}
          videoSx={{ borderRadius: pxToRem(32) }}
          src={leader.video?.file.url}
          playButtonType="white-center"
          poster={leader.video.file.thumbUrl || undefined}
          posterSx={{ borderRadius: pxToRem(32) }}
          posterShadowSx={{ display: "none" }}
          onPlay={() =>
            trackEvent("Watched Leader Profile Video", {
              leaderUid: leader.uid,
            })
          }
        />
      </Box>
    ) : null;

    const profileButton = (
      <TrackedButton
        component={Link}
        startIcon={<Icon icon={faUser} />}
        target={isMobile ? undefined : "_blank"}
        trackingName={"leader_link_activity_header"}
        to={leaderPath(leader.uid, leader.leader_link)}
        sx={(theme: Theme) => ({
          color: "black",
          width: "fit-content",
          [theme.breakpoints.down("md")]: {
            width: "100%",
          },
        })}
        color="neutral"
      >
        <Box>{t`Profile`}</Box>
      </TrackedButton>
    );

    return (
      <Box>
        <Box
          sx={(theme: Theme) => ({
            gap: "1.6rem",
            alignItems: "center",
            justifyContent: "space-between",
            display: "flex",
            flexWrap: "wrap",
            marginBottom: 32,
            [theme.breakpoints.down("md")]: {
              display: "block",
            },
          })}
        >
          <Box
            flex
            sx={(theme: Theme) => ({
              flexDirection: "column",
              gap: "1.6rem",
              width: "39rem",
              [theme.breakpoints.down("md")]: {
                width: "100%",
              },
            })}
          >
            <TeacherNameAndRatings
              leader={leader}
              onClickViewTeacher={onClickViewTeacher}
              onClickViewReviews={onClickViewReviews}
              isTeacherDetails={true}
              emphasizedTeacherName={true}
            />
            {isMobile && video}
            {!hideActions && (
              <Box
                flex
                sx={(theme: Theme) => ({
                  gap: pxToRem(8),
                  justifyContent: "start",
                  width: "22rem",
                  height: theme.typography.pxToRem(44),
                  [theme.breakpoints.down("md")]: {
                    justifyContent: "center",
                    width: "100%",
                  },
                })}
              >
                {profileButton}
                <AskAQuestionButton activity={activity} leader={leader} />
              </Box>
            )}
          </Box>
          {!isMobile && video}
        </Box>
        <Typography variant="h5" sx={{ marginY: 12 }} ref={credentialsRef}>
          {t("Teacher expertise and credentials")}
        </Typography>
        {credentials.length != 0 && (
          <Credentials
            credentials={credentials}
            isOrg={!!leader.ownerOfSellerOrg}
          />
        )}
        <Box>
          <Box
            component={"pre"}
            sx={{
              maxHeight: expandExpertise ? undefined : 120,
              overflow: expandExpertise ? "visible" : "hidden",
              ...gradientStyles,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: "1.6rem",
                fontWeight: 400,
                lineHeight: "2.4rem",
              }}
            >
              {translatedTeacherExpertise
                ? translatedTeacherExpertise
                : translatedAboutText}
            </Typography>
          </Box>
          {collapseText && (
            <ShowMoreModal
              buttonLabel={t("Show More")}
              trackingName={"teacher_details"}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <Typography variant="h4" emphasized>
                  {activity?.isClub
                    ? t`Group Leader`
                    : leader.ownerOfSellerOrg
                    ? t`Offered by`
                    : t`Meet the teacher`}
                </Typography>
                <TeacherNameAndRatings
                  leader={leader}
                  onClickViewTeacher={onClickViewTeacher}
                  onClickViewReviews={onClickViewReviews}
                  isTeacherDetails={true}
                  emphasizedTeacherName={true}
                />
                {(translatedTeacherExpertise || credentials.length != 0) && (
                  <Box
                    component={"pre"}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 16,
                      paddingY: 20,
                    }}
                  >
                    <Typography variant="h5">
                      {t("Teacher expertise and credentials")}
                    </Typography>
                    {credentials.length != 0 && (
                      <Credentials
                        credentials={credentials}
                        isOrg={!!leader.ownerOfSellerOrg}
                      />
                    )}
                    {translatedTeacherExpertise}
                  </Box>
                )}
                <Box
                  component={"pre"}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                    paddingY: 20,
                    margin: "unset",
                  }}
                >
                  <Typography variant="h5">{t("About me")}</Typography>
                  {translatedAboutText}
                </Box>
                <Box
                  flex
                  sx={(theme: Theme) => ({
                    gap: pxToRem(8),
                    justifyContent: "start",
                    width: "22rem",
                    height: "4.8rem",
                    [theme.breakpoints.down("md")]: {
                      justifyContent: "center",
                      width: "100%",
                    },
                  })}
                >
                  {profileButton}
                  <AskAQuestionButton activity={activity} leader={leader} />
                </Box>
              </Box>
            </ShowMoreModal>
          )}
        </Box>
      </Box>
    );
  },
);
